import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, SearchIcon, ShieldCheckIcon, ClockIcon, MinusCircleIcon, CheckCircle2Icon, UserPlusIcon, HashIcon, ChevronDownIcon } from 'lucide-react';
import { User, LicenseData, MichiganLicenseData, StateStatus, createDefaultLicense, createDefaultMichiganLicense } from './types';
interface StateLicenseModalProps {
  stateName: string;
  isOpen: boolean;
  onClose: () => void;
  users: User[];
  licenses: Record<string, Record<string, LicenseData>>;
  michiganLicenses: Record<string, MichiganLicenseData>;
  onUpdateLicense: (userId: string, stateName: string, license: LicenseData) => void;
  onUpdateMichiganLicense: (userId: string, license: MichiganLicenseData) => void;
  onAddUserToState: (userId: string, stateName: string) => void;
  onOpenUserStateDetail: (userId: string, stateName: string) => void;
}
const STATUS_CONFIG: Record<StateStatus, {
  label: string;
  icon: typeof CheckCircle2Icon;
  color: string;
}> = {
  not_started: {
    label: 'Not Started',
    icon: MinusCircleIcon,
    color: 'text-slate-500'
  },
  in_progress: {
    label: 'In Progress',
    icon: ClockIcon,
    color: 'text-amber-400'
  },
  complete: {
    label: 'Licensed',
    icon: CheckCircle2Icon,
    color: 'text-emerald-400'
  }
};
export function StateLicenseModal({
  stateName,
  isOpen,
  onClose,
  users,
  licenses,
  michiganLicenses,
  onUpdateLicense,
  onUpdateMichiganLicense,
  onAddUserToState,
  onOpenUserStateDetail
}: StateLicenseModalProps) {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'assigned' | 'unassigned'>('assigned');
  const [showAddDropdown, setShowAddDropdown] = useState(false);
  const [addSearch, setAddSearch] = useState('');
  const isMichigan = stateName === 'Michigan';
  const getUserStatus = (userId: string): StateStatus => {
    if (isMichigan) {
      const mi = michiganLicenses[userId];
      if (!mi) return 'not_started';
      if (mi.steps.every(Boolean)) return 'complete';
      if (mi.steps.some(Boolean)) return 'in_progress';
      return 'not_started';
    }
    return licenses[userId]?.[stateName]?.status || 'not_started';
  };
  const isUserAssigned = (userId: string): boolean => {
    if (isMichigan) return !!michiganLicenses[userId];
    return !!licenses[userId]?.[stateName];
  };
  const assignedUsers = useMemo(() => {
    const q = search.toLowerCase().trim();
    return users.filter(u => isUserAssigned(u.id)).filter(u => !q || u.name.toLowerCase().includes(q) || u.nmlsId.toLowerCase().includes(q)).sort((a, b) => {
      const aStatus = getUserStatus(a.id);
      const bStatus = getUserStatus(b.id);
      const order: Record<StateStatus, number> = {
        complete: 0,
        in_progress: 1,
        not_started: 2
      };
      return order[aStatus] - order[bStatus] || a.name.localeCompare(b.name);
    });
  }, [users, licenses, michiganLicenses, search, stateName]);
  const unassignedUsers = useMemo(() => {
    const q = addSearch.toLowerCase().trim();
    return users.filter(u => !isUserAssigned(u.id)).filter(u => !q || u.name.toLowerCase().includes(q) || u.nmlsId.toLowerCase().includes(q)).sort((a, b) => a.name.localeCompare(b.name));
  }, [users, licenses, michiganLicenses, addSearch, stateName]);
  const counts = useMemo(() => {
    let licensed = 0;
    let inProgress = 0;
    let total = 0;
    for (const user of users) {
      if (!isUserAssigned(user.id)) continue;
      total++;
      const status = getUserStatus(user.id);
      if (status === 'complete') licensed++;else if (status === 'in_progress') inProgress++;
    }
    return {
      licensed,
      inProgress,
      total
    };
  }, [users, licenses, michiganLicenses, stateName]);
  const handleQuickStatusChange = (userId: string, newStatus: StateStatus) => {
    if (isMichigan) {
      const existing = michiganLicenses[userId] || createDefaultMichiganLicense();
      if (newStatus === 'complete') {
        onUpdateMichiganLicense(userId, {
          ...existing,
          steps: [true, true, true, true, true, true]
        });
      } else if (newStatus === 'not_started') {
        onUpdateMichiganLicense(userId, {
          ...existing,
          steps: [false, false, false, false, false, false]
        });
      }
    } else {
      const existing = licenses[userId]?.[stateName] || createDefaultLicense();
      onUpdateLicense(userId, stateName, {
        ...existing,
        status: newStatus
      });
    }
  };
  const handleAddUser = (userId: string) => {
    onAddUserToState(userId, stateName);
    setAddSearch('');
  };
  if (!isOpen) return null;
  return <AnimatePresence data-id="element-249" data-mp="2c99agc16avb8">
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-250" data-mp="2c8pswwffq8xq">
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="absolute inset-0 bg-black/60 backdrop-blur-sm" data-id="element-251" data-mp="24pggsvfjnw4z" />
        <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 10
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 10
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className={`relative w-full max-w-2xl bg-slate-900 border rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh] ${isMichigan ? 'border-amber-500/30 shadow-[0_0_40px_rgba(245,158,11,0.1)]' : 'border-slate-700/50'}`} data-id="element-252" data-mp="1qyblu6n4fs0z">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800" data-id="element-253" data-mp="zqbikr7n4gx4">
            <div data-id="element-254" data-mp="14qg10ms3zlpz">
              <h2 className="text-xl font-bold text-white" data-id="element-255" data-mp="14ov62w1vu0rl">{stateName}</h2>
              <div className="flex items-center gap-3 mt-1" data-id="element-256" data-mp="qqey71saa47v">
                {counts.licensed > 0 && <span className="flex items-center gap-1 text-xs text-emerald-400" data-id="element-257" data-mp="ur79e0kpuj6s">
                    <ShieldCheckIcon className="w-3 h-3" data-id="element-258" data-mp="h0loy7fm616s" />
                    {counts.licensed} licensed
                  </span>}
                {counts.inProgress > 0 && <span className="flex items-center gap-1 text-xs text-amber-400" data-id="element-259" data-mp="725z07sboxyv">
                    <ClockIcon className="w-3 h-3" data-id="element-260" data-mp="70ba6idxklpi" />
                    {counts.inProgress} in progress
                  </span>}
                <span className="text-xs text-slate-500" data-id="element-261" data-mp="3u6luuoca7175">
                  {counts.total} assigned
                </span>
              </div>
            </div>
            <button onClick={onClose} className="p-2 -mr-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-full transition-colors" data-id="element-262" data-mp="32do3ofgifqc">
              <XIcon className="w-5 h-5" data-id="element-263" data-mp="3an6gu3lvi0hg" />
            </button>
          </div>

          {/* Add User Dropdown */}
          <div className="px-6 py-3 border-b border-slate-800/50" data-id="element-264" data-mp="3kj9fv0lfhj06">
            <div className="relative" data-id="element-265" data-mp="3khoofjueug6j">
              <button onClick={() => setShowAddDropdown(!showAddDropdown)} className="flex items-center gap-2 px-3.5 py-2 bg-emerald-600/10 hover:bg-emerald-600/20 border border-emerald-500/30 text-emerald-400 text-sm font-medium rounded-xl transition-colors w-full justify-center" data-id="element-266" data-mp="2dd8os7g75ny">
                <UserPlusIcon className="w-4 h-4" data-id="element-267" data-mp="75efvzpre674" />
                Add Users to {stateName}
                <ChevronDownIcon className={`w-4 h-4 transition-transform ${showAddDropdown ? 'rotate-180' : ''}`} data-id="element-268" data-mp="l3cvt9jqaaoq" />
              </button>

              <AnimatePresence data-id="element-269" data-mp="1nhqygup20cx6">
                {showAddDropdown && <motion.div initial={{
                opacity: 0,
                y: -4,
                height: 0
              }} animate={{
                opacity: 1,
                y: 0,
                height: 'auto'
              }} exit={{
                opacity: 0,
                y: -4,
                height: 0
              }} className="mt-2 bg-slate-800/80 border border-slate-700/50 rounded-xl overflow-hidden" data-id="element-270" data-mp="1njl7pbqyqglf">
                    <div className="p-2" data-id="element-271" data-mp="1dpulkdib6i8w">
                      <div className="flex items-center bg-slate-900/50 rounded-lg px-3 py-2 mb-2" data-id="element-272" data-mp="1iinvrgwjqisd">
                        <SearchIcon className="w-3.5 h-3.5 text-slate-500 mr-2" data-id="element-273" data-mp="l1ytxn7ljgiz" />
                        <input type="text" value={addSearch} onChange={e => setAddSearch(e.target.value)} placeholder="Search unassigned users…" className="w-full bg-transparent text-sm text-slate-200 focus:outline-none placeholder:text-slate-500" autoFocus data-id="element-274" data-mp="q1l57r3v6mp5" />
                      </div>
                    </div>
                    <div className="max-h-48 overflow-y-auto custom-scrollbar px-2 pb-2 space-y-0.5" data-id="element-275" data-mp="3vou8448ejim3">
                      {unassignedUsers.length === 0 ? <div className="text-center py-4 text-xs text-slate-500" data-id="element-276" data-mp="3qwarsziby9dl">
                          {users.length === 0 ? 'No users created yet' : 'All users are already assigned'}
                        </div> : unassignedUsers.map(user => <button key={user.id} onClick={() => handleAddUser(user.id)} className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-700/50 transition-colors text-left" data-id="element-277" data-mp="380knh5mxd0wj">
                            <div className="w-7 h-7 rounded-lg bg-slate-700/50 flex items-center justify-center text-[10px] font-bold text-slate-400" data-id="element-278" data-mp="2vrkgtp3u87t4">
                              {user.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-279" data-mp="283krmtm6cpgn">
                              <span className="text-sm text-slate-200 truncate block" data-id="element-280" data-mp="285dxi6oyr5nl">
                                {user.name}
                              </span>
                              <span className="text-[10px] text-slate-500 tabular-nums" data-id="element-281" data-mp="3jhogxmhthpfd">
                                #{user.nmlsId}
                              </span>
                            </div>
                            <UserPlusIcon className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" data-id="element-282" data-mp="3w1v6xnvoqa3t" />
                          </button>)}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </div>

          {/* Search Assigned Users */}
          <div className="px-6 py-2.5 border-b border-slate-800/30" data-id="element-283" data-mp="ilw5ebplaadn">
            <div className="flex items-center bg-slate-800/50 border border-slate-700/50 rounded-lg px-3 py-2 focus-within:border-emerald-500/50 transition-all" data-id="element-284" data-mp="io9xcboz36gz">
              <SearchIcon className="w-3.5 h-3.5 text-slate-500 mr-2" data-id="element-285" data-mp="8v1jj8g99csq" />
              <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search assigned users…" className="w-full bg-transparent text-sm text-slate-200 focus:outline-none placeholder:text-slate-500" data-id="element-286" data-mp="wlf30akry3bp" />
            </div>
          </div>

          {/* User List */}
          <div className="flex-1 overflow-y-auto custom-scrollbar p-4 space-y-1.5" data-id="element-287" data-mp="127pbqxjam977">
            {assignedUsers.length === 0 ? <div className="text-center py-12 space-y-2" data-id="element-288" data-mp="x7t9b2q8uptr">
                <p className="text-sm text-slate-400" data-id="element-289" data-mp="s9rpgfkzumh8">
                  No users assigned to {stateName}
                </p>
                <p className="text-xs text-slate-500" data-id="element-290" data-mp="nit594wvx48p">
                  Use the button above to add users
                </p>
              </div> : assignedUsers.map(user => {
            const status = getUserStatus(user.id);
            const config = STATUS_CONFIG[status];
            const StatusIcon = config.icon;
            return <div key={user.id} className="flex items-center gap-3 px-4 py-3 bg-slate-800/30 hover:bg-slate-800/50 border border-slate-700/40 rounded-xl transition-all group" data-id="element-291" data-mp="4jp22cs0y4oy">
                    {/* Avatar */}
                    <div className="w-9 h-9 rounded-lg bg-slate-700/50 border border-slate-600/50 flex items-center justify-center text-xs font-bold text-slate-300 flex-shrink-0" data-id="element-292" data-mp="4eg9ozeztylz">
                      {user.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0" data-id="element-293" data-mp="3hs1hq7624u6p">
                      <span className="text-sm font-medium text-white truncate block" data-id="element-294" data-mp="3mkkw36ckjtck">
                        {user.name}
                      </span>
                      <div className="flex items-center gap-1" data-id="element-295" data-mp="2p4f5ybmoej8r">
                        <HashIcon className="w-2.5 h-2.5 text-slate-500" data-id="element-296" data-mp="2u2qe8hxbolov" />
                        <span className="text-[10px] text-slate-500 tabular-nums" data-id="element-297" data-mp="zn0cfp2ispf5">
                          {user.nmlsId}
                        </span>
                      </div>
                    </div>

                    {/* Quick Status Toggle */}
                    {!isMichigan && <div className="flex bg-slate-800/80 rounded-lg p-0.5 gap-0.5 flex-shrink-0" data-id="element-298" data-mp="14mwgfob7wgup">
                        <button onClick={() => handleQuickStatusChange(user.id, 'not_started')} className={`p-1.5 rounded-md transition-colors ${status === 'not_started' ? 'bg-slate-700 text-slate-200' : 'text-slate-500 hover:text-slate-300'}`} aria-label="Set not started" data-id="element-299" data-mp="1idrkmnaru9ry">
                          <MinusCircleIcon className="w-3.5 h-3.5" data-id="element-300" data-mp="3u4daxgtjgnyr" />
                        </button>
                        <button onClick={() => handleQuickStatusChange(user.id, 'in_progress')} className={`p-1.5 rounded-md transition-colors ${status === 'in_progress' ? 'bg-amber-500/20 text-amber-400' : 'text-slate-500 hover:text-slate-300'}`} aria-label="Set in progress" data-id="element-301" data-mp="3p5skg9439ini">
                          <ClockIcon className="w-3.5 h-3.5" data-id="element-302" data-mp="1ztdj113vny5f" />
                        </button>
                        <button onClick={() => handleQuickStatusChange(user.id, 'complete')} className={`p-1.5 rounded-md transition-colors ${status === 'complete' ? 'bg-emerald-500/20 text-emerald-400' : 'text-slate-500 hover:text-slate-300'}`} aria-label="Set complete" data-id="element-303" data-mp="1ly1n4qdzk1mx">
                          <CheckCircle2Icon className="w-3.5 h-3.5" data-id="element-304" data-mp="3bbez91scatyl" />
                        </button>
                      </div>}

                    {/* Michigan status badge */}
                    {isMichigan && <div className={`flex items-center gap-1.5 px-2 py-1 rounded-lg ${status === 'complete' ? 'bg-emerald-500/10' : status === 'in_progress' ? 'bg-amber-500/10' : 'bg-slate-800/50'}`} data-id="element-305" data-mp="34t21isowmduh">
                        <StatusIcon className={`w-3.5 h-3.5 ${config.color}`} data-id="element-306" data-mp="3sjxz66g1k39n" />
                        <span className={`text-xs font-medium ${config.color}`} data-id="element-307" data-mp="3sm214iudqs7a">
                          {config.label}
                        </span>
                      </div>}

                    {/* Detail button */}
                    <button onClick={() => onOpenUserStateDetail(user.id, stateName)} className="p-1.5 text-slate-500 hover:text-white hover:bg-slate-700 rounded-lg transition-colors opacity-0 group-hover:opacity-100" aria-label={`View details for ${user.name}`} data-id="element-308" data-mp="3errkkosje4cb">
                      <ChevronDownIcon className="w-4 h-4 -rotate-90" data-id="element-309" data-mp="1t3p9jdgrkh2l" />
                    </button>
                  </div>;
          })}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-slate-800 flex justify-end" data-id="element-310" data-mp="27140immo0wwo">
            <button onClick={onClose} className="px-6 py-2 bg-slate-800 hover:bg-slate-700 text-white text-sm font-medium rounded-xl transition-colors" data-id="element-311" data-mp="26z12bfbczxgv">
              Close
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>;
}